'use strict';

import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {useSelector} from 'react-redux';
import _ from 'lodash';
import {keys} from 'ts-transformer-keys';


import Form, {
    IChangeEvent
} from '@rjsf/core';
import ErrorList from '@rjsf/core/lib/components/templates/ErrorList';
import {
    createSchemaUtils,
    englishStringTranslator,
    ErrorSchema,
    RJSFSchema,
    RJSFValidationError
} from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';


import {
    InputTextarea,
    InputTextareaProps
} from 'primereact/inputtextarea';
import {Toolbar} from 'primereact/toolbar';

import {__} from '~/shared/src/utilities/messageCatalog';
import {useHookFormDelay} from '~/shared/src/utilities/modules/form';
import {dataExtraction} from '~/shared/src/utilities/dataExtraction';
import {
    LoadConfigParameters as CalendarLoadConfigParameters,
    LoadConfigResult as CalendarLoadConfigResult
} from '~/shared/src/utilities/modules/calendar';
import {
    LoadConfigParameters as OverviewLoadConfigParameters,
    LoadConfigResult as OverviewLoadConfigResult
} from '~/shared/src/utilities/modules/overview';
import {DataStructure} from '~/shared/src/utilities/dataStructure';
import {
    ColumnFilter,
    ColumnSorter
} from '~/shared/src/validationSchemes/modules/overview';

import FieldWrapper, {
    DefaultProps as FieldWrapperDefaultProps,
    defaultProps as fieldWrapperDefaultProps,
    Props as FieldWrapperProps,
    ComponentProps as FieldWrapperComponentProps,
    componentProps as fieldWrapperComponentProps
} from '~/frontend/src/components/base/shared/field/FieldWrapper';
import FiltersField from '~/frontend/src/components/base/shared/field/valueTypeFields/JsonField/SpecialFields/FiltersField';
import SortersField from '~/frontend/src/components/base/shared/field/valueTypeFields/JsonField/SpecialFields/SortersField';

import xhr from '~/frontend/src/utilities/xhr';
import {StoreState} from '~/frontend/src/store';
import {getHeightStyle} from '~/frontend/src/components/base/shared/field/valueTypeFields/TextField';
import {
    getToolbarComponents,
    ToolbarComponents,
    ToolbarLayoutComponents
} from '~/frontend/src/utilities/toolbars';
import {Variables} from '~/frontend/src/components/base/Form';


type InputProps = Omit<InputTextareaProps, 'autoComplete' | 'onChange' | 'key'>
const inputProps = keys<InputProps>();

type InputChangeProps = React.ChangeEvent<HTMLTextAreaElement>;

type SpecialFieldData = {
    dataStructureForField : DataStructure;
    columnDefinitions : OverviewLoadConfigResult['data']['columnDefinitions'];
    defaultFilters? : OverviewLoadConfigResult['data']['defaultFilters'];
    defaultFixFilters? : OverviewLoadConfigResult['data']['fixFilters'];
    defaultSorters? : OverviewLoadConfigResult['data']['defaultSorters'];
};

type SpecialFieldConfig = {
    viewType : 'calendar' | 'calendarView' | 'chart' | 'chartView' | 'overview' | 'overviewView';
    viewFieldId : number;
    viewElementId : number;
    viewKey : string;
    type : 'columns' | 'filters' | 'fixfilters' | 'sorters';
    dataStructureForValue? : DataStructure;
};


type DefaultProps = FieldWrapperDefaultProps & {

    /**
     * The number of text rows to be displayed within the field.
     */
    height? : number;

    /**
     * The max number of text rows to be displayed within the field.
     * 0 means unlimited
     */
    maxHeight? : number;

    /**
     * Should the field marked if dirty.
     */
    markDirty? : boolean;

    /**
     * The CSS class names to style the input field with.
     */
    inputClassNames? : string[];

    /**
     * The styles to style the input field with.
     */
    inputStyle? : React.CSSProperties;

    /**
     * true to use react-hook-form with debounced onChange event
     */
    useHookForm? : boolean;
};
const defaultProps : Required<DefaultProps> = {
    ...fieldWrapperDefaultProps,

    disabled       : false,
    height         : 3,
    maxHeight      : 0,
    markDirty      : false,
    inputClassNames: [],
    inputStyle     : {},
    useHookForm    : false
};

type Props = Omit<FieldWrapperProps, 'pt'> & {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The value.
     */
    value? : string;

    id? : string;

    /**
     * The value to determine dirty against.
     */
    originalValue? : string;

    jsonSchema? : RJSFSchema;
    uiSchema? : { [ key : string ] : any };

    /**
     * Fired on change of input immediately if useHookForm is false, debounced if it's true
     * @param value
     * @param originalEvent
     */
    onChange : ( value : string | undefined, originalEvent : any ) => void;

    /**
     * Fired on change of input immediately if useHookForm is true otherwise never fired
     * @param value
     * @param originalEvent
     */
    onIntermediateChange? : ( value : string | undefined, originalEvent : any ) => void;

    onInvalid? : ( errors : string[] ) => void;
    onIntermediateInvalid? : ( errors : string[] ) => void;

    onValid? : () => void;
    onIntermediateValid? : () => void;

    pt? : {
        fieldWrapper? : FieldWrapperProps[ 'pt' ];
    };

    variables? : Variables;
};

export type ComponentProps = InputProps & DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = InputProps & Required<DefaultProps> & Props;


const JsonField = ( props : AllProps ) => {
    // avoid rendering if hidden
    if ( props.visibility === 'hidden' ) {
        return null;
    }
    else {
        let ignoreCursorPosition : boolean = false;

        const {
                  uid,
                  //tabIndex,
                  disabled,
                  height,
                  id,
                  infoTextLayout,
                  inputClassNames,
                  inputStyle,
                  labelPosition,
                  labelVisibility,
                  markDirty,
                  maxHeight,
                  messagePlaceholders,
                  messageTemplateStringResolver,
                  onBlur,
                  onChange,
                  onFocus,
                  onIntermediateChange,
                  onIntermediateInvalid,
                  onIntermediateValid,
                  onInvalid,
                  onValid,
                  originalValue,
                  placeholder,
                  pt,
                  tabIndex,
                  uiSchema,
                  useHookForm,
                  value,
                  variables
              }                                             = props,
              user                                          = useSelector( ( state : StoreState ) => state.session?.user ),
              elementId                                     = id ?? `${uid}_jsonField`,
              inputRef                                      = useRef<HTMLTextAreaElement>( null ),
              inValue                                       = useRef<string | undefined>( value ),
              outValue                                      = useRef<string | undefined>( value ),
              timeoutId                                     = useRef<NodeJS.Timeout | undefined>(),
              validTimeoutId                                = useRef<NodeJS.Timeout | undefined>(),
              specialFieldData                              = useRef<SpecialFieldData | undefined>(),
              [ elementValue, setElementValue ]             = useState<string | undefined>( value ),
              [ editorMode, setEditorMode ]                 = useState<'editor' | 'text'>( 'editor' ),
              [ focus, setFocus ]                           = useState<boolean>( false ),
              [ cursorPosition, setCursorPosition ]         = useState<number>( 0 ),
              [ nextCursorPosition, setNextCursorPosition ] = useState<number>( 0 ),
              [ lastEditorValue, setLastEditorValue ]       = useState<string>( '' ),
              [ errors, setErrors ]                         = useState<RJSFValidationError[]>( [] ),
              [ errorSchema, setErrorSchema ]               = useState<ErrorSchema | undefined>( undefined ),
              specialFieldConfig                            = useMemo(
                  () => {
                      let retVal : SpecialFieldConfig | undefined;

                      if ( variables?.formDataContext && variables?.formRootItemId && variables?.propertyTypeName ) {
                          const formRootItem = global.data[ variables.formDataContext ]?.items[ variables.formRootItemId ];

                          switch ( formRootItem?.itemType.name ) {
                              case 'CalendarView':
                                  retVal = {
                                      viewType             : 'calendarView',
                                      viewFieldId          : variables.fieldItemId,
                                      viewElementId        : variables.formRootItemId,
                                      viewKey              : dataExtraction.extractReferencedItemKey(
                                          formRootItem,
                                          variables.formDataContext,
                                          [
                                              { direction: 'DestToSrc', referenceType: 'belongsTo', targetItemType: 'Calendar' }
                                          ]
                                      ) ?? '',
                                      type                 : variables.propertyTypeName.toLowerCase(),
                                      dataStructureForValue: variables.dataStructure
                                  };
                                  break;

                              case 'Chart':
                                  retVal = {
                                      viewType             : 'chart',
                                      viewFieldId          : variables.fieldItemId,
                                      viewElementId        : variables.formRootItemId,
                                      viewKey              : global.data[ variables.formDataContext ]?.items[ variables.formRootItemId ]?.key ?? '',
                                      type                 : variables.propertyTypeName.toLowerCase(),
                                      dataStructureForValue: variables.dataStructure
                                  };

                                  specialFieldData.current = {
                                      dataStructureForField: variables.dataStructure,
                                      columnDefinitions    : variables.columnDefinitions
                                  };
                                  break;

                              case 'ChartView':
                                  retVal = {
                                      viewType             : 'chartView',
                                      viewFieldId          : variables.fieldItemId,
                                      viewElementId        : variables.formRootItemId,
                                      viewKey              : dataExtraction.extractReferencedItemKey(
                                          formRootItem,
                                          variables.formDataContext,
                                          [
                                              { direction: 'DestToSrc', referenceType: 'belongsTo', targetItemType: 'Chart' }
                                          ]
                                      ) ?? '',
                                      type                 : variables.propertyTypeName.toLowerCase(),
                                      dataStructureForValue: variables.dataStructure
                                  };
                                  break;

                              case 'OverviewView':
                                  retVal = {
                                      viewType             : 'overviewView',
                                      viewFieldId          : variables.fieldItemId,
                                      viewElementId        : variables.formRootItemId,
                                      viewKey              : dataExtraction.extractReferencedItemKey(
                                          formRootItem,
                                          variables.formDataContext,
                                          [
                                              { direction: 'DestToSrc', referenceType: 'belongsTo', targetItemType: 'Overview' }
                                          ]
                                      ) ?? '',
                                      type                 : variables.propertyTypeName.toLowerCase(),
                                      dataStructureForValue: variables.dataStructure
                                  };
                                  break;
                          }
                      }

                      return retVal;
                  },
                  [
                      variables?.dataStructure,
                      variables?.formDataContext,
                      variables?.formRootItemId,
                      variables?.propertyTypeName
                  ]
              ),
              jsonSchema                                    = useMemo(
                  () => {
                      let retVal = undefined;

                      if ( props.jsonSchema ) {
                          retVal = props.jsonSchema;
                      }

                      // convert enum: [ { code: <code>, description: '<description>' }, ... ]
                      //  to enumNames: [ '<description>', ... ]
                      //  and enum: [ <code>, ... ]
                      // resolve messages in strings
                      if ( retVal ) {
                          const recursiveProcessEnum = ( part : any ) => {
                              if ( typeof part === 'object' ) {
                                  const partKeys = Object.keys( part );

                                  for ( const partKey of partKeys ) {
                                      if ( partKey === 'enum' ) {
                                          if ( Array.isArray( part.enum ) ) {
                                              const enumNames : string[] = [],
                                                    newEnum : string[]   = [];

                                              let converted = false;

                                              for ( let i = 0; i < part.enum.length; i++ ) {
                                                  if ( typeof part.enum[ i ] === 'object' ) {
                                                      converted = true;

                                                      enumNames.push( __( part.enum[ i ].description ) ?? part.enum[ i ].value ?? part.enum[ i ].code );
                                                      newEnum.push( part.enum[ i ].value ?? part.enum[ i ].code );
                                                  }
                                                  else {
                                                      enumNames.push( part.enum[ i ] );
                                                      newEnum.push( part.enum[ i ] );
                                                  }
                                              }

                                              if ( converted ) {
                                                  part.enumNames = enumNames;
                                                  part.enum = newEnum;
                                              }
                                          }
                                      }
                                      else if ( typeof part[ partKey ] === 'string' ) {
                                          part[ partKey ] = __( part[ partKey ] );
                                      }
                                      else {
                                          recursiveProcessEnum( part[ partKey ] );
                                      }
                                  }
                              }
                          };

                          recursiveProcessEnum( retVal );
                      }

                      return retVal;
                  },
                  [
                      props.jsonSchema
                  ]
              ),
              heightStyle                                   = useMemo(
                  () => getHeightStyle( inputStyle, height, maxHeight, labelVisibility, labelPosition, infoTextLayout ),
                  [
                      height,
                      infoTextLayout,
                      inputStyle.height,
                      labelPosition,
                      labelVisibility,
                      maxHeight
                  ]
              ),
              messageResolver                               = useMemo(
                  () => {
                      if ( !messageTemplateStringResolver ) {
                          return ( messageKey : string ) : string | undefined => messageKey === '' ? undefined : __( messageKey, messagePlaceholders, false );
                      }
                      else {
                          return ( messageKey : string ) => messageKey === '' ? undefined : __( messageTemplateStringResolver( __( messageKey, messagePlaceholders ) ) ?? '', undefined, false );
                      }
                  },
                  [
                      messagePlaceholders,
                      messageTemplateStringResolver
                  ]
              ),
              resolvedPlaceholder                           = useMemo(
                  () => {
                      return placeholder ? messageResolver( placeholder ) : undefined;
                  },
                  [
                      JSON.stringify( placeholder ),
                      messageResolver,
                      user ? user.language : undefined
                  ]
              ),
              validateValue                                 = useCallback(
                  ( newValue : string ) => {
                      let retVal : RJSFValidationError[] = [],
                          newErrorSchema;

                      // validate against jsonSchema if
                      if ( jsonSchema ) {
                          try {
                              const parsedNewValue  = JSON.parse( newValue ),
                                    schemaUtils     = createSchemaUtils( validator, jsonSchema ),
                                    retrievedSchema = schemaUtils.retrieveSchema( jsonSchema, parsedNewValue );

                              ( { errors: retVal, errorSchema: newErrorSchema } = schemaUtils.getValidator().validateFormData( parsedNewValue, retrievedSchema ) );

                              if ( retVal.length ) {
                                  setErrors( retVal );
                                  setErrorSchema( newErrorSchema );
                              }
                              else {
                                  setErrors( [] );
                                  setErrorSchema( undefined );
                              }
                          }
                          catch ( e ) {
                              // do nothing
                          }
                      }

                      return retVal;
                  },
                  [
                      jsonSchema
                  ]
              ),
              onIntermediateError                           = useCallback(
                  ( newErrors : RJSFValidationError[] ) => {
                      if ( onIntermediateInvalid && newErrors.length ) {
                          onIntermediateInvalid( newErrors.map( e => e.stack ) );
                      }
                  },
                  [
                      onIntermediateInvalid
                  ]
              ),
              onError                                       = useCallback(
                  ( newErrors : RJSFValidationError[] ) => {
                      if ( onInvalid && newErrors.length ) {
                          onInvalid( newErrors.map( e => e.stack ) );
                      }
                  },
                  [
                      onInvalid
                  ]
              ),
              onEditorChange                                = useCallback(
                  ( event : IChangeEvent ) => {
                      let changedValue = event.formData;

                      event = {
                          ...event,
                          currentTarget: document.getElementById( elementId )
                      } as IChangeEvent & { currentTarget : HTMLElement };

                      try {
                          changedValue = JSON.stringify( changedValue );
                      }
                      catch ( e ) {
                          changedValue = changedValue.replace( /\u00a0/gim, ' ' );
                      }

                      setErrors( [] );
                      setErrorSchema( undefined );

                      if ( changedValue !== outValue.current ) {
                          setElementValue( changedValue );
                          setCursorPosition( 0 );

                          if ( useHookForm ) {
                              onIntermediateChange?.( changedValue, event );

                              if ( timeoutId.current ) {
                                  clearTimeout( timeoutId.current );
                              }
                              timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                          }
                          else {
                              onChange( changedValue, event );
                          }
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange,
                      useHookForm
                  ]
              ),
              onEditorError                                 = useCallback(
                  ( editorErrors : RJSFValidationError[] ) => {
                      setErrors( editorErrors );
                      setErrorSchema( undefined );
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onInputChange                                 = useCallback(
                  ( event : InputChangeProps ) => {
                      let changedValue = event.target.value;

                      changedValue = changedValue.replace( /\u00a0/gim, ' ' );

                      try {
                          const parsedChangedValue = JSON.parse( changedValue.replace( /\u00a0/gim, ' ' ) );

                          if ( elementValue && jsonSchema !== undefined && uiSchema !== undefined ) {
                              const parsedElementValue     = JSON.parse( elementValue ),
                                    parsedElementValueKeys = Object.keys( parsedElementValue ) as string[];

                              for ( const pEVKey of parsedElementValueKeys ) {
                                  if ( uiSchema[ pEVKey ]?.[ 'ui:style' ]?.display === 'none' ) {
                                      parsedChangedValue[ pEVKey ] = parsedElementValue[ pEVKey ];
                                  }
                              }
                          }

                          changedValue = JSON.stringify( parsedChangedValue );
                      }
                      catch ( e ) {
                          // do nothing
                      }

                      if ( changedValue !== outValue.current ) {
                          const newErrors = validateValue( changedValue );

                          setElementValue( changedValue );

                          if ( !newErrors.length ) {
                              if ( useHookForm ) {
                                  onIntermediateChange?.( changedValue, event );

                                  if ( timeoutId.current ) {
                                      clearTimeout( timeoutId.current );
                                  }
                                  timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                              }
                              else {
                                  onChange( changedValue, event );
                              }
                          }
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange,
                      useHookForm
                  ]
              ),
              onInputFocus                                  = useCallback(
                  ( event : React.FocusEvent<HTMLTextAreaElement> ) => {
                      setFocus( true );

                      onFocus?.( event );
                  },
                  [
                      onFocus
                  ]
              ),
              onInputBlur                                   = useCallback(
                  ( event : React.FocusEvent<HTMLTextAreaElement> ) => {
                      setFocus( false );

                      setElementValue( outValue.current );

                      if ( timeoutId.current ) {
                          clearTimeout( timeoutId.current );
                      }

                      if ( !errors.length && outValue.current !== inValue.current ) {
                          onChange( outValue.current, event );
                      }

                      onBlur?.( event );
                  },
                  [
                      errors,
                      onBlur,
                      onChange
                  ]
              ),
              onInputTextareaKey                            = useCallback(
                  ( event : React.KeyboardEvent<HTMLTextAreaElement> ) => {
                      if ( event.type === 'keydown' ) {
                          // add a line break on enter, but only once (keyup), but always stop event
                          if ( event.key === 'Enter' ) {
                              const lineBreak         = '\n',
                                    currentValue      = ( inputRef.current as unknown as HTMLTextAreaElement )?.value,
                                    selectionStart    = ( inputRef.current as unknown as HTMLTextAreaElement )?.selectionStart,
                                    selectionEnd      = ( inputRef.current as unknown as HTMLTextAreaElement )?.selectionEnd,
                                    changedValue      = currentValue.slice( 0, selectionStart ) + lineBreak + currentValue.slice( selectionEnd ),
                                    newCursorPosition = selectionStart + lineBreak.length,
                                    newErrors         = validateValue( changedValue );

                              ( inputRef.current as unknown as HTMLTextAreaElement ).value = changedValue;
                              ( inputRef.current as unknown as HTMLTextAreaElement ).selectionStart = newCursorPosition;
                              ( inputRef.current as unknown as HTMLTextAreaElement ).selectionEnd = newCursorPosition;

                              setElementValue( changedValue );
                              setNextCursorPosition( ( inputRef.current as unknown as HTMLTextAreaElement )?.selectionStart ?? 0 );

                              if ( !newErrors.length ) {
                                  if ( useHookForm ) {
                                      onIntermediateChange?.( changedValue, event );

                                      if ( timeoutId.current ) {
                                          clearTimeout( timeoutId.current );
                                      }
                                      timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                                  }
                                  else {
                                      onChange( changedValue, event );
                                  }
                              }

                              event.preventDefault();
                              event.stopPropagation();
                          }
                          else if ( !ignoreCursorPosition && !event.metaKey && ![ 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'Backspace', 'Meta' ].includes( event.key ) ) {
                              setNextCursorPosition( ( ( inputRef.current as unknown as HTMLTextAreaElement )?.selectionStart ?? -1 ) + 1 );
                          }
                          else if ( nextCursorPosition !== 0 ) {
                              setNextCursorPosition( 0 );
                          }
                      }
                      else {
                          if ( event.metaKey || event.key === 'Meta' ) {
                              if ( cursorPosition !== 0 ) {
                                  setCursorPosition( 0 );
                              }
                          }
                          else if ( !ignoreCursorPosition && cursorPosition !== nextCursorPosition ) {
                              setCursorPosition( nextCursorPosition );
                          }

                          if ( nextCursorPosition !== 0 ) {
                              setNextCursorPosition( 0 );
                          }

                          ignoreCursorPosition = false;
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange,
                      useHookForm
                  ]
              ),
              getEditor                                     = () : React.ReactElement => {
                  let editor : React.ReactElement | undefined,
                      editorValue = '';

                  if ( specialFieldConfig ) {
                      let originalEditorValue = '';

                      if ( elementValue ) {
                          try {
                              const parsedElementValue = JSON.parse( elementValue );

                              if ( uiSchema !== undefined ) {
                                  const parsedElementValueKeys = Object.keys( parsedElementValue ) as string[];

                                  for ( const pEVKey of parsedElementValueKeys ) {
                                      if ( uiSchema[ pEVKey ]?.[ 'ui:style' ]?.display === 'none' ) {
                                          delete parsedElementValue[ pEVKey ];
                                      }
                                  }
                              }

                              editorValue = parsedElementValue;
                          }
                          catch ( e ) {
                              // do nothing
                          }
                      }

                      if ( originalValue ) {
                          try {
                              const parsedElementValue = JSON.parse( originalValue );

                              if ( uiSchema !== undefined ) {
                                  const parsedElementValueKeys = Object.keys( parsedElementValue ) as string[];

                                  for ( const pEVKey of parsedElementValueKeys ) {
                                      if ( uiSchema[ pEVKey ]?.[ 'ui:style' ]?.display === 'none' ) {
                                          delete parsedElementValue[ pEVKey ];
                                      }
                                  }
                              }

                              originalEditorValue = parsedElementValue;
                          }
                          catch ( e ) {
                              // do nothing
                          }
                      }

                      switch ( specialFieldConfig.type ) {
                          case 'columns':
                              break;

                          case 'filters':
                          case 'fixfilters':
                              editor = <FiltersField
                                  uid={elementId}
                                  columnDefinitions={specialFieldData.current?.columnDefinitions ?? []}
                                  dataStructureForField={specialFieldData.current?.dataStructureForField ?? { origId: 0 } as DataStructure}
                                  dataStructureForValue={specialFieldConfig.dataStructureForValue}
                                  defaultFilters={specialFieldConfig.type !== 'fixfilters' ? specialFieldData.current?.defaultFilters ?? [] : specialFieldData.current?.defaultFixFilters ?? []}
                                  value={editorValue ? editorValue as unknown as ColumnFilter[] : undefined}
                                  originalValue={originalEditorValue ? originalEditorValue as unknown as ColumnFilter[] : undefined}
                                  onChange={( changedValue : ColumnFilter[] | undefined ) => {
                                      onInputChange( {
                                          target       : {
                                              value: changedValue
                                                  ? JSON.stringify( changedValue )
                                                  : ''
                                          },
                                          currentTarget: document.getElementById( elementId )
                                      } as InputChangeProps );
                                  }}
                                  markDirty={markDirty}
                                  disabled={disabled}
                                  tabIndex={tabIndex}
                              />;
                              break;

                          case 'sorters':
                              editor = <SortersField
                                  uid={elementId}
                                  columnDefinitions={specialFieldData.current?.columnDefinitions ?? []}
                                  defaultSorters={specialFieldData.current?.defaultSorters ?? []}
                                  value={editorValue ? editorValue as unknown as ColumnSorter[] : undefined}
                                  originalValue={originalEditorValue ? originalEditorValue as unknown as ColumnSorter[] : undefined}
                                  onChange={( changedValue : ColumnSorter[] | undefined ) => {
                                      onInputChange( {
                                          target       : {
                                              value: changedValue
                                                  ? JSON.stringify( changedValue )
                                                  : ''
                                          },
                                          currentTarget: document.getElementById( elementId )
                                      } as InputChangeProps );
                                  }}
                                  markDirty={markDirty}
                                  disabled={disabled}
                                  tabIndex={tabIndex}
                              />;
                              break;
                      }

                      if ( editor ) {
                          editor = <div
                              id={uid}
                              className={`yn-jsonfield-wrapper${!height ? ' yn-auto-height' : ''}`}
                              style={{ ...defaultProps.inputStyle, ...inputStyle, ...heightStyle }}
                          >
                              {editor}
                          </div>
                      }
                  }

                  if ( !editor ) {
                      if ( jsonSchema !== undefined && editorMode !== 'text' ) {
                          if ( elementValue ) {
                              try {
                                  editorValue = JSON.parse( elementValue );
                              }
                              catch ( e ) {
                                  // do nothing
                              }
                          }

                          editor = <Form
                              id={elementId}

                              formData={elementValue ? editorValue : {}}
                              onChange={onEditorChange}
                              onError={onEditorError}

                              schema={jsonSchema}
                              uiSchema={{
                                  'ui:rootFieldId'            : 'editor-root',
                                  'ui:fieldReplacesAnyOrOneOf': true,
                                  'ui:submitButtonOptions'    : {
                                      norender: true
                                  },
                                  ...uiSchema
                              }}
                              validator={validator}

                              showErrorList={infoTextLayout === 'text' ? 'top' : false}
                              liveValidate={true}
                              liveOmit={true}
                              omitExtraData={true}
                              disabled={disabled}
                              readonly={disabled}
                          />;
                      }
                      else {
                          const currentEditor = document.getElementById( elementId );

                          if ( elementValue ) {
                              try {
                                  const parsedElementValue = JSON.parse( elementValue );

                                  if ( uiSchema !== undefined ) {
                                      const parsedElementValueKeys = Object.keys( parsedElementValue ) as string[];

                                      for ( const pEVKey of parsedElementValueKeys ) {
                                          if ( uiSchema[ pEVKey ]?.[ 'ui:style' ]?.display === 'none' ) {
                                              delete parsedElementValue[ pEVKey ];
                                          }
                                      }
                                  }

                                  editorValue = JSON.stringify( parsedElementValue, null, '\u00a0\u00a0\u00a0\u00a0' );
                              }
                              catch ( e ) {
                                  // preserve given format
                                  editorValue = elementValue.replace( / /gim, '\u00a0' );
                              }
                          }

                          // fix cursor position
                          if ( currentEditor && currentEditor.tagName === 'TEXTAREA' && lastEditorValue !== ( editorValue ?? '' ) ) {
                              const currentValue          = ( currentEditor as HTMLTextAreaElement ).value,
                                    currentCursorPosition = ( inputRef.current as unknown as HTMLTextAreaElement )?.selectionStart,
                                    findRegExp            = new RegExp( currentValue?.substring( currentCursorPosition - 5, currentCursorPosition + 1 ).replace( ':', '\\:\\s?' ), 'gm' );

                              findRegExp.lastIndex = currentCursorPosition - 1;

                              const findResult = findRegExp.exec( editorValue );

                              if ( findResult?.[ 0 ] ) {
                                  ignoreCursorPosition = true;

                                  setCursorPosition( findResult.index + findResult[ 0 ].replace( '\n', '' ).length );

                                  if ( nextCursorPosition !== 0 ) {
                                      setNextCursorPosition( 0 );
                                  }
                              }
                          }

                          if ( lastEditorValue !== editorValue ) {
                              setLastEditorValue( editorValue ?? '' );
                          }

                          editor = <InputTextarea
                              {..._.pick( props, inputProps ) as Omit<InputProps, 'onChange'>}

                              id={elementId}

                              ref={inputRef}

                              value={editorValue ?? ''}

                              onChange={onInputChange}

                              pt={{
                                  root: {
                                      onFocus  : onInputFocus,
                                      onBlur   : onInputBlur,
                                      onKeyUp  : onInputTextareaKey,
                                      onKeyDown: onInputTextareaKey
                                  }
                              }}

                              className={`yn-jsonfield yn-jsonfield-plaintext ${inputClassNames.join( ' ' )}`}
                              style={{ ...defaultProps.inputStyle, ...inputStyle, ...heightStyle }}

                              placeholder={resolvedPlaceholder}
                              tooltip={undefined}
                              tooltipOptions={undefined}

                              aria-describedby={`${elementId}_message`}
                          />;

                          if ( infoTextLayout === 'text' && errors.length ) {
                              editor = <>
                                  <div className="yn-jsonfield-error-wrapper">
                                      <ErrorList
                                          errors={errors}
                                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                          // @ts-ignore
                                          errorSchema={errorSchema}
                                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                          // @ts-ignore
                                          registry={{
                                              translateString: englishStringTranslator
                                          }}
                                      />
                                  </div>
                                  {editor}
                              </>
                          }
                      }
                  }

                  if ( jsonSchema !== undefined ) {
                      const layoutComponents : ToolbarLayoutComponents = {
                          left : [],
                          right: []
                      };

                      layoutComponents.left.push( {
                          type  : 'buttonSet',
                          params: {
                              key: `${uid}_panelButtons`,

                              buttons: [
                                  {
                                      type  : 'button',
                                      params: {
                                          key: `${uid}_editorButton`,

                                          label    : __( 'YN.base.jsonField.editorMode.editor.label' ),
                                          onClick  : () => {
                                              setEditorMode( 'editor' )
                                          },
                                          className: editorMode === 'editor' ? '' : 'p-button-outlined'
                                      }
                                  },
                                  {
                                      type  : 'button',
                                      params: {
                                          key: `${uid}_textButton`,

                                          label    : __( 'YN.base.jsonField.editorMode.text.label' ),
                                          onClick  : () => {
                                              setEditorMode( 'text' )
                                          },
                                          className: editorMode === 'text' ? '' : 'p-button-outlined'
                                      }
                                  }
                              ]
                          }
                      } );

                      const toolbarComponents : ToolbarComponents = getToolbarComponents( layoutComponents );

                      editor = <div
                          className={`yn-jsonfield yn-jsonfield-editor ${inputClassNames.join( ' ' )} ${disabled ? 'yn-disabled' : ''} ${focus ? 'p-focus' : ''}`}
                          style={{ ...defaultProps.inputStyle, ...inputStyle, ...heightStyle }}
                      >
                          <Toolbar
                              start={toolbarComponents.left}
                              end={toolbarComponents.right}
                              className="yn-panel-toolbar yn-toolbar-horizontal-separator-bottom"
                              style={{ zIndex: 6 }}
                          />
                          {editor}
                      </div>;
                  }

                  return editor;
              };

        inValue.current = value;
        outValue.current = elementValue;

        // load special field data
        useEffect(
            () => {
                if ( specialFieldConfig ) {
                    let loadFnc : ( () => Promise<void> ) | undefined;

                    switch ( specialFieldConfig.viewType ) {
                        case 'calendar':
                            break;

                        case 'calendarView':
                            loadFnc = async () => {
                                const response = await xhr.post(
                                    `${BASE_URL}/api/calendar/loadConfig`,
                                    {
                                        viewKey: specialFieldConfig.viewKey
                                    } as CalendarLoadConfigParameters,
                                    undefined,
                                    uid,
                                    'loadSpecialFieldData'
                                );

                                if ( response?.data ) {
                                    const config = response.data as CalendarLoadConfigResult;

                                    specialFieldData.current = {
                                        dataStructureForField: config.data.dataStructure,
                                        columnDefinitions    : []
                                    };
                                }
                            };
                            break;

                        case 'chart':
                            break;

                        case'chartView':
                            break;

                        case 'overview':
                            break;

                        case 'overviewView':
                            loadFnc = async () => {
                                const response = await xhr.post(
                                    `${BASE_URL}/api/overview/loadConfig`,
                                    {
                                        viewKey: specialFieldConfig.viewKey
                                    } as OverviewLoadConfigParameters,
                                    undefined,
                                    uid,
                                    'loadSpecialFieldData'
                                );

                                if ( response?.data ) {
                                    const config = response.data as OverviewLoadConfigResult;

                                    specialFieldData.current = {
                                        dataStructureForField: config.data.dataStructure,
                                        columnDefinitions    : config.data.columnDefinitions,
                                        defaultFilters       : config.data.defaultFilters,
                                        defaultFixFilters    : config.data.fixFilters,
                                        defaultSorters       : config.data.defaultSorters
                                    };
                                }
                            };
                            break;
                    }

                    if ( loadFnc ) {
                        void loadFnc();
                    }
                }
            },
            [
                specialFieldConfig
            ]
        );

        useEffect(
            () => {
                if ( !focus ) {
                    setElementValue( value );
                }
            },
            [
                value
            ]
        );

        useEffect(
            () => {
                if ( errors.length ) {
                    if ( useHookForm ) {
                        onIntermediateError( errors );

                        if ( validTimeoutId.current ) {
                            clearTimeout( validTimeoutId.current );
                        }
                        validTimeoutId.current = setTimeout( onError.bind( null, errors ), useHookFormDelay );
                    }
                    else {
                        onError( errors );
                    }
                }
                else {
                    if ( useHookForm ) {
                        onIntermediateValid?.();

                        if ( onValid ) {
                            if ( validTimeoutId.current ) {
                                clearTimeout( validTimeoutId.current );
                            }
                            validTimeoutId.current = setTimeout( onValid, useHookFormDelay );
                        }
                    }
                    else {
                        onValid?.();
                    }
                }
            },
            [
                errors
            ]
        );

        // set cursor position
        // set height if autoHeight
        useLayoutEffect(
            () => {
                const editor = document.getElementById( elementId );

                if ( editor && editor.tagName === 'TEXTAREA' ) {
                    if ( cursorPosition !== 0 ) {
                        ( editor as HTMLTextAreaElement ).setSelectionRange( cursorPosition, cursorPosition );
                        setCursorPosition( 0 );
                    }

                    if ( height === 0 ) {
                        if ( editor?.scrollHeight ) {
                            editor.style.height = '2.2rem';
                            editor.style.height = `${editor.scrollHeight + 2}px`;
                        }
                    }
                }
            }
        );

        return <FieldWrapper
            {..._.pick( props, fieldWrapperComponentProps ) as FieldWrapperComponentProps}

            uid={elementId}

            isDirty={markDirty && !specialFieldConfig && ( elementValue ?? '' ) !== ( originalValue ?? '' )}
            errorMessage={infoTextLayout !== 'text' ? props.errorMessage : undefined}

            autoComplete={false}
            pt={pt?.fieldWrapper}
        >
            {getEditor()}
        </FieldWrapper>;
    }
};
JsonField.defaultProps = defaultProps;

export default JsonField;
